import { Dispatch } from "@reduxjs/toolkit";
import axios from "axios";
import { Message, SendMessagePayload, ChatResponse, ChatResponseContent, SearchFilters } from "../interfaces/chat";
import { axiosClient } from "../config/axios";
import { api } from "../config/api";
import { mapAgentFilters } from "../helpers/agentFilterMapper";
import type { AgentFilters } from "../interfaces/agentFilters";
import type { AddCommentPayload } from "../interfaces/omuni";
import {
    sendMessageRequest,
    sendMessageSuccess,
    sendMessageFailure,
    addUserMessage,
    toggleChat as toggleChatReducer,
    openChat as openChatReducer,
    closeChat as closeChatReducer,
    clearMessages as clearMessagesReducer,
    sendFeedbackRequest,
    sendFeedbackSuccess,
    sendFeedbackFailure,
} from "../reducers/chat";
import { createTask, addComment } from "./omuni";

// Queue to prevent concurrent requests to the agents API (avoids 409 conflicts)
let agentRequestQueue = Promise.resolve();
const enqueueAgentRequest = <T>(fn: () => Promise<T>): Promise<T> => {
    const result = agentRequestQueue.then(fn);
    agentRequestQueue = result.then(() => undefined, () => undefined);
    return result;
};

const AGENTS_BASE_URL = process.env.REACT_APP_AGENTS_URL || "https://agents-core.globalthink.io";
const AGENTS_CONVERSATION_ID = process.env.REACT_APP_AGENTS_CONVERSATION_ID || "69a0a27c011838a5ad7eb285";
const AGENTS_API_KEY = process.env.REACT_APP_AGENTS_API_KEY || "080FC782-ED56-4EC5-AAB6-8015C3F68E5D";

const generateMessageId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const normalizeAccents = (text: string): string => {
    return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ñ/g, 'n')
        .replace(/Ñ/g, 'N');
};

const buildPublicationsUrl = (filters: any): string => {
    let url = "";

    if (filters?.brand?.length) {
        url += `${url ? '&' : ''}brand=${filters.brand.join(",")}`;
    }
    if (filters?.dealership?.length) {
        url += `${url ? '&' : ''}dealership=${filters.dealership.join(",")}`;
    }
    if (filters?.model?.length) {
        url += `${url ? '&' : ''}model=${filters.model.join(",")}`;
    }
    if (filters?.year?.length) {
        url += `${url ? '&' : ''}year=1,${filters.year.join(",")}`;
    }
    if (filters?.price?.min && filters?.price?.max) {
        url += `${url ? '&' : ''}price=${filters.price.min},${filters.price.max}`;
    } else if (filters?.price?.min) {
        url += `${url ? '&' : ''}price=${filters.price.min},*`;
    } else if (filters?.price?.max) {
        url += `${url ? '&' : ''}price=*,${filters.price.max}`;
    }
    if (filters?.mileage?.length) {
        url += `${url ? '&' : ''}mileage=${filters.mileage[0]?.value}`;
    }
    if (filters?.color?.length) {
        url += `${url ? '&' : ''}color=${filters.color.join(",")}`;
    }
    if (filters?.fuel?.length) {
        url += `${url ? '&' : ''}fuel=${filters.fuel.join(",")}`;
    }
    if (filters?.region?.length) {
        url += `${url ? '&' : ''}region=${encodeURIComponent(filters.region.join(","))}`;
    }
    if (filters?.transmission?.length && filters.transmission.length < 3) {
        url += `${url ? '&' : ''}transmission=${filters.transmission.join(",")}`;
    }

    if (filters?.vehicleType === 'new') {
        url += `${url ? '&' : ''}condition=new`;
    } else if (filters?.vehicleType === 'used') {
        url += `${url ? '&' : ''}condition=used`;
    }

    if (filters?.type?.length) {
        const types = filters.type.map((t: any) => normalizeAccents(t)).join(",");
        url += `${url ? '&' : ''}type=${types}`;
    }

    const shouldSkipTypeArticle = filters?.vehicleCategory?.length === 2;
    if (!shouldSkipTypeArticle) {
        const categoryMap: Record<string, string> = { 'Autos': 'vehicles', 'Comerciales': 'trucks' };
        const typeArticle = filters?.vehicleCategory?.length
            ? (categoryMap[filters.vehicleCategory[0]] || 'vehicles')
            : 'vehicles';
        url += `${url ? '&' : ''}type_articles=${typeArticle}`;
    }

    return url;
};

const mapChatFiltersToMappedFilters = (chatFilters: SearchFilters) => {
    const agentFilters: AgentFilters = {
        tipo: chatFilters.tipo,
        uso: chatFilters.uso,
        tipoVenta: chatFilters.tipoVenta,
        concesionario: chatFilters.concesionario,
        marca: chatFilters.marca,
        modelo: chatFilters.modelo,
        año: chatFilters.anno,
        color: chatFilters.color,
        transmision: chatFilters.transmision,
        carroceria: chatFilters.carroceria,
        combustible: chatFilters.combustible,
        region: chatFilters.region,
        precio: chatFilters.precio,
        kilometraje: chatFilters.kilometraje,
        version: chatFilters.version,
    };

    return mapAgentFilters(agentFilters, [], []);
};

export const sendMessage = (messageContent: string, caseId: string) => {
    return async (dispatch: any, getState: any) => {
        const state = getState();
        const isFirstMessage = state.chat.messages.length === 0;
        const user = state.auth?.user;
        const taskId = state.omuni?.taskId;

        const senderInfo = user
            ? {
                senderId: user._id,
                senderUsername: user.email || user.name,
                senderDisplayName: `${user.name || ''} ${user.last_name || ''}`.trim() || 'Usuario',
            }
            : {
                senderId: caseId,
                senderUsername: 'visitante',
                senderDisplayName: 'Visitante Web',
            };

        const userMessage: Message = {
            id: generateMessageId(),
            role: "user",
            content: messageContent,
            timestamp: Date.now(),
        };

        dispatch(addUserMessage(userMessage));
        dispatch(sendMessageRequest());

        // Create task on first message and wait for taskId
        if (isFirstMessage && !taskId) {
            try {
                await dispatch(createTask({
                    source: "web",
                    description: "Chat web - Linze IA",
                    status: {
                        statusId: "69bc15d27dafca455d8be9eb",
                    },
                    contact: {
                        userName: user?.name || state.chat.caseId,
                        userLastName: user?.last_name || "Visitante",
                    },
                }));
            } catch { }
        }

        // Send user message as comment (use latest taskId from state)
        const activeTaskId = getState().omuni?.taskId;
        if (activeTaskId) {
            const userCommentPayload: AddCommentPayload = {
                comment: messageContent,
                channel: 'web',
                ...senderInfo,
                direction: 'incoming',
                type: 'external',
                created: new Date().toISOString(),
            };
            try {
                dispatch(addComment(activeTaskId, userCommentPayload));
            } catch { }
        }

        await enqueueAgentRequest(async () => {
            try {
                const payload: SendMessagePayload = {
                    message: {
                        type: "text",
                        content: messageContent,
                    },
                    caseId,
                    sync: true,
                };
                const response = await axios.post<ChatResponse>(
                    `${AGENTS_BASE_URL}/conversations/${AGENTS_CONVERSATION_ID}`,
                    payload,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `ApiKey ${AGENTS_API_KEY}`,
                        },
                    }
                );

                const parsed: ChatResponseContent = JSON.parse(response.data.content);

                let publications: any[] | undefined;

                if (parsed.type === 'search' && parsed.filters) {
                    try {
                        const mappedFilters = mapChatFiltersToMappedFilters(parsed.filters!);
                        const chatUrl = buildPublicationsUrl(mappedFilters);
                        const chatResponse = await axiosClient.get(`${api.publications}/search?page=1&limit=3&fallback=true&pictures=original&${chatUrl}`);
                        const responseData = chatResponse?.data?.data;
                        publications = responseData?.found === false ? [] : (responseData?.publications || []);
                    } catch (searchError) {
                        console.error('Error fetching publications for chat:', searchError);
                    }
                }

                const assistantMessage: Message = {
                    id: generateMessageId(),
                    role: "assistant",
                    content: parsed.message,
                    timestamp: Date.now(),
                    responseType: parsed.type || "message",
                    filters: parsed.filters,
                    filtersApplied: parsed.filtersApplied,
                    busquedaOriginal: parsed.busquedaOriginal,
                    publications,
                };

                dispatch(sendMessageSuccess(assistantMessage));

                // Send AI message as comment
                const currentTaskId = getState().omuni?.taskId;
                if (currentTaskId) {
                    const aiCommentPayload: AddCommentPayload = {
                        comment: parsed.message,
                        channel: 'web',
                        senderId: 'linze-ia',
                        senderUsername: 'linze.ia',
                        senderDisplayName: 'Linze IA',
                        direction: 'outgoing',
                        type: 'external',
                        created: new Date().toISOString(),
                    };
                    try {
                        dispatch(addComment(currentTaskId, aiCommentPayload));
                    } catch { }
                }
            } catch (error: any) {
                const is409 = error?.response?.status === 409;
                const errorText = is409
                    ? "Hay otra consulta en proceso. Por favor, esperá un momento e intentá de nuevo."
                    : "Hubo un error al comunicarse con el asistente. Por favor, intentá de nuevo.";

                dispatch(sendMessageFailure(errorText));
            }
        });
    };
};

export const sendHiddenContextMessage = (contextMessage: string, caseId: string) => {
    return async (_dispatch: any) => {
        await enqueueAgentRequest(async () => {
            try {
                const payload: SendMessagePayload = {
                    message: {
                        type: "text",
                        content: contextMessage,
                    },
                    caseId,
                    sync: true,
                };

                await axios.post<ChatResponse>(
                    `${AGENTS_BASE_URL}/conversations/${AGENTS_CONVERSATION_ID}`,
                    payload,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `ApiKey ${AGENTS_API_KEY}`,
                        },
                    }
                );
            } catch (error) {
                console.error('Error sending publication context:', error);
            }
        });
    };
};

export const toggleChat = () => {
    return (dispatch: Dispatch) => {
        dispatch(toggleChatReducer());
    };
};

export const openChat = () => {
    return (dispatch: Dispatch) => {
        dispatch(openChatReducer());
    };
};

export const closeChat = () => {
    return (dispatch: Dispatch) => {
        dispatch(closeChatReducer());
    };
};

export const clearMessages = () => {
    return (dispatch: Dispatch) => {
        dispatch(clearMessagesReducer());
    };
};

export const sendConversationFeedback = (caseId: string, liked: boolean) => {
    return async (dispatch: any) => {
        dispatch(sendFeedbackRequest());
        try {
            await axiosClient.post(`${api.marketing}/conversation-feedback`, { caseId, liked });
            dispatch(sendFeedbackSuccess(liked));
        } catch {
            dispatch(sendFeedbackFailure());
        }
    };
};
